import * as React from 'react';

import {BannerReact} from 'common/components/banner';
import {BannerType} from 'common/components/banner/types';
import {Visibility} from 'common/components/base/stacking-context';
import {ButtonReact} from 'common/components/button';
import {ButtonTheme, ButtonType} from 'common/components/button/constants';
import {InputReact} from 'common/components/input';
import {MarkdownReact} from 'common/components/markdown';
import {ModalReact, ModalType} from 'common/components/modal';
import type {ModalChangeEvent} from 'common/components/modal/types';
import {useTranslation} from 'common/i18n/hooks';
import {withStyles} from 'common/util/react';
import type {LocaleSafeString} from 'common/i18n';
import {formatTextReact} from 'common/util/format-text-react';
import {ENTER_KEY_CODE} from 'common/util/dom';

import {ConfirmParams, THEME, ValidatorFn} from './types';
import css from './index.styl?inline';
import translationKeys from './index.i18n';

export type ChangeEvent = CustomEvent<{state: string}>;

export type SubmitEvent = CustomEvent<{action: `confirm`; inputValue: string} | {action: `cancel`}>;

export interface ConfirmProps {
  /** allows the input field to be submitted empty */
  allowEmptyInputValue?: boolean;
  /** content rendered in place of the slot/children */
  bannerContent?: React.ReactNode;
  /** displays an info banner at the bottom of the modal content */
  bottomBannerText?: LocaleSafeString;
  cancelButtonLabel?: LocaleSafeString;
  children?: React.ReactNode;
  className?: string;
  /** modal closeable (x in top right) */
  closeable?: boolean;
  confirmButtonDisabled?: boolean;
  confirmButtonLabel?: LocaleSafeString;
  /** text in the body of the modal */
  description?: LocaleSafeString;
  /** allow escaped newlines in the description text */
  descriptionAllowNewline?: boolean;
  /** render the description as markdown; mutually exclusive with descriptionAllowNewline */
  descriptionUseMarkdown?: boolean;
  /** the string value for test reference */
  elref?: string;
  /** sizes the modal to its content instead of a fixed height */
  growToContent?: boolean;
  hideCancelButton?: boolean;
  id?: string;
  initialInputValue?: string;
  /** validates the input value, returning an error message or null */
  inputValidator?: ValidatorFn;
  modalType?: ModalType;
  notFullscreen?: boolean;
  onChange?: (ev: ChangeEvent) => void;
  /** Invoked when a `submit` was not `preventDefault`ed. Only {@link UncontrolledConfirm} sets it. */
  onRequestClose?: () => void;
  onSubmit?: (ev: SubmitEvent) => void;
  open?: boolean;
  /** translatable overrides for title, description and the confirm label */
  params?: ConfirmParams;
  role?: string;
  showInput?: boolean;
  style?: React.CSSProperties;
  /** modal theme (e.g. delete) */
  theme?: THEME;
  title?: LocaleSafeString;
}

export function Confirm({
  allowEmptyInputValue,
  bannerContent,
  bottomBannerText,
  cancelButtonLabel,
  children,
  className,
  closeable,
  confirmButtonDisabled,
  confirmButtonLabel,
  description,
  descriptionAllowNewline,
  descriptionUseMarkdown,
  elref,
  growToContent,
  hideCancelButton,
  id,
  initialInputValue = ``,
  inputValidator,
  modalType,
  notFullscreen,
  onChange,
  onRequestClose,
  onSubmit,
  open,
  params,
  role,
  showInput,
  style,
  theme = THEME.CONFIRM,
  title,
}: ConfirmProps) {
  const {t, print} = useTranslation(translationKeys);
  const [inputValue, setInputValue] = React.useState(initialInputValue);
  const [inputError, setInputError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setInputValue(initialInputValue);
  }, [initialInputValue]);

  const isValidInputState = allowEmptyInputValue || Boolean(inputValue?.trim());
  const isConfirmButtonDisabled = Boolean(confirmButtonDisabled) || Boolean(showInput && !isValidInputState);

  const dispatchSubmit = (detail: SubmitEvent[`detail`]) => {
    const event = new CustomEvent(`submit`, {cancelable: true, detail}) as SubmitEvent;
    onSubmit?.(event);
    if (!event.defaultPrevented) {
      onRequestClose?.();
    }
  };

  const handleConfirm = () => {
    // The validator's verdict is used directly rather than read back from state, which React has
    // not committed yet at this point.
    const error = inputValidator ? inputValidator(inputValue) : null;
    setInputError(error);

    if (!error) {
      dispatchSubmit({action: `confirm`, inputValue});
    }
  };

  const handleCancel = () => dispatchSubmit({action: `cancel`});

  const handleModalChange = (ev: ModalChangeEvent) => {
    // Only the modal's own visibility events are forwarded. Two other things reach this listener:
    // `change` is a native HTML event, so the input's own `change` bubbles up carrying no detail;
    // and any nested stackable (a drop menu inside the dialog) reports its own open/closed state on
    // the way past. Either one read as the dialog closing resolves imperative callers as `cancel`.
    const target = ev.target as HTMLElement | null;
    if (!ev.detail || !(`state` in ev.detail) || target?.tagName?.toLowerCase() !== `mp-modal`) {
      return;
    }
    onChange?.(new CustomEvent(`change`, {detail: ev.detail}) as ChangeEvent);
  };

  const handleInput = (value: string, ev: React.FormEvent) => {
    ev.stopPropagation();
    setInputValue(value);
    setInputError(inputValidator ? inputValidator(value) : null);
  };

  const resolvedTitle = params?.title ? print(params.title) : title;
  const resolvedDescription = params?.description ? print(params.description) : description;

  const confirmLabel = params?.confirmButtonLabel
    ? print(params.confirmButtonLabel)
    : confirmButtonLabel
      ? confirmButtonLabel
      : theme === THEME.DELETE
        ? t(`common.delete`)
        : t(`common.okay`);

  return (
    <ModalReact
      className={`mp-confirm-modal${className ? ` ${className}` : ``}`}
      style={style}
      closeable={Boolean(closeable)}
      open={Boolean(open)}
      growToContent={growToContent}
      notFullscreen={notFullscreen}
      modalType={modalType}
      onChange={handleModalChange}
    >
      <div className='mp-confirm-container' elref={elref} id={id} role={role} aria-label={resolvedTitle}>
        <div className='mp-confirm-body'>
          <div className='mp-confirm-body-content'>
            {resolvedTitle ? <div className='mp-confirm-title'>{resolvedTitle}</div> : null}
            {resolvedDescription ? (
              descriptionAllowNewline ? (
                <div className='mp-confirm-description-newline'>
                  {formatTextReact(resolvedDescription, {className: `confirm-description-newline`})}
                </div>
              ) : descriptionUseMarkdown ? (
                <div className='mp-confirm-description-newline'>
                  <MarkdownReact text={resolvedDescription} />
                </div>
              ) : (
                <div className='mp-confirm-description'>{resolvedDescription}</div>
              )
            ) : null}
            {showInput ? (
              <InputReact
                className='mp-confirm-mp-input'
                autoFocus
                elref='confirmInput'
                errorMessage={inputError ?? undefined}
                validationStatus={inputError ? `invalid` : ``}
                onInput={handleInput}
                onKeyup={({keyCode}) => {
                  if (keyCode === ENTER_KEY_CODE && !isConfirmButtonDisabled) {
                    handleConfirm();
                  }
                }}
                value={inputValue}
              />
            ) : null}
            {bottomBannerText ? (
              <BannerReact
                className='mp-confirm-info-banner-bottom'
                theme='info'
                type={BannerType.Embedded}
                bodyText={bottomBannerText}
              />
            ) : null}
            {bannerContent || <slot>{children}</slot>}
          </div>
        </div>
        <div className='mp-confirm-buttons'>
          {!hideCancelButton && theme !== THEME.INFO ? (
            <ButtonReact
              theme={ButtonTheme.Tertiary}
              elref='mpConfirmCancelButton'
              className='mp-confirm-button-cancel'
              onClick={handleCancel}
            >
              {cancelButtonLabel || t(`common.cancel`)}
            </ButtonReact>
          ) : null}
          <ButtonReact
            elref='mpConfirmConfirmButton'
            disabled={isConfirmButtonDisabled}
            theme={ButtonTheme.Primary}
            type={theme === THEME.DELETE ? ButtonType.Destructive : ButtonType.Info}
            className='mp-confirm-button-accept'
            onClick={handleConfirm}
          >
            {confirmLabel}
          </ButtonReact>
        </div>
      </div>
    </ModalReact>
  );
}

/**
 * Owns `open` on behalf of `<ConfirmEl>` callers, who set it as an attribute and expect the dialog to
 * take itself down when a `submit` is not `preventDefault`ed. `custom-property-modal` relies on the
 * modal `change` that closing produces to clear its own `toDelete` state.
 *
 * `ConfirmReact` is controlled instead, so the two entry points need different owners for this.
 */
export function UncontrolledConfirm({open, ...props}: ConfirmProps) {
  const [isOpen, setIsOpen] = React.useState(open);

  React.useEffect(() => {
    setIsOpen(open);
  }, [open]);

  return <Confirm {...props} open={isOpen} onRequestClose={() => setIsOpen(false)} />;
}

/**
 * `panel.ts` applies `withStyles` to `UncontrolledConfirm` separately, so each entry point wraps
 * exactly once and the stylesheet is never injected twice into one tree.
 */
export const ConfirmReact = withStyles(Confirm, css);

/**
 * What an imperatively-opened dialog can resolve with. `close` is deliberately distinct from
 * `cancel`, because callers can mean different things by them: for `promptSuperadminOverrideSave`,
 * `cancel` means "make a copy" while dismissing the dialog means "do nothing at all".
 */
export type ConfirmResolution = SubmitEvent[`detail`] | {action: `close`};

/**
 * Owns `open` for the imperative entry points, which have no parent to control it, and funnels both
 * ways the dialog can end into the single `submit` event `promisifyModal` resolves on.
 *
 * The `change` -> `Closed` leg is load-bearing. `promisifyModal` arbitrates its own closure events
 * (escape, outside click, the close button) by matching `stackId` against the element it created,
 * and the element it creates here is a bare `div`, which has none. Without this leg the promise
 * would never settle for a `closeable` dialog — and `prompt()` is `closeable` by default.
 */
export function ImperativeConfirm({
  onResolve,
  ...props
}: Partial<ConfirmProps> & {onResolve: (detail: ConfirmResolution) => void}) {
  const [open, setOpen] = React.useState(true);
  const [isDismissed, setIsDismissed] = React.useState(false);
  const hasResolved = React.useRef(false);

  /**
   * Closing the dialog makes the modal report `Closed`, which is also the cancel signal, so the two
   * legs would otherwise race: the click arrives on a custom element rather than through React's
   * synthetic system, so `setOpen(false)` can flush synchronously and turn a confirm into a cancel.
   */
  const resolveOnce = (detail: ConfirmResolution) => {
    if (hasResolved.current) {
      return;
    }
    hasResolved.current = true;
    onResolve(detail);
  };

  // `promisifyModal` keeps the element it created for another half second so the close can animate.
  // Rendering nothing once the dialog has finished closing keeps its buttons out of reach of a
  // caller that opens a second dialog inside that window.
  if (isDismissed) {
    return null;
  }

  return (
    <ConfirmReact
      {...props}
      open={open}
      onSubmit={(ev: SubmitEvent) => {
        resolveOnce(ev.detail);
        setOpen(false);
      }}
      onChange={(ev: ChangeEvent) => {
        if (ev.detail?.state === Visibility.Closed) {
          resolveOnce({action: `close`});
          setIsDismissed(true);
        }
      }}
    />
  );
}
